<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import { home } from '@/routes';

defineProps<{
    title?: string;
    description?: string;
}>();
</script>

<template>
    <div class="relative min-h-svh overflow-hidden bg-background">
        <div class="pointer-events-none absolute inset-0">
            <div
                class="absolute top-[-8rem] left-1/2 h-[24rem] w-[24rem] -translate-x-1/2 rounded-full bg-brand/[0.08] blur-3xl sm:h-[32rem] sm:w-[32rem] lg:top-[-10rem] lg:left-[12%] lg:translate-x-0"
            />
            <div
                class="absolute right-[-8rem] bottom-[-10rem] h-[20rem] w-[20rem] rounded-full bg-brand/[0.06] blur-3xl dark:bg-brand/[0.1]"
            />
            <div
                class="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.94),_transparent_55%)] dark:bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.2),_transparent_52%)]"
            />
        </div>

        <div
            class="relative mx-auto flex min-h-svh w-full max-w-6xl items-center px-4 py-10 sm:px-6 lg:px-8"
        >
            <div
                class="grid w-full gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(24rem,28rem)] lg:items-center lg:gap-14"
            >
                <div class="flex flex-col gap-6 text-center lg:text-left">
                    <div class="flex justify-center lg:justify-start">
                        <Link
                            :href="home()"
                            class="inline-flex items-center gap-3 rounded-full border border-border/60 bg-background/80 px-4 py-2 text-sm font-medium text-foreground shadow-sm backdrop-blur-sm transition-colors hover:border-brand/30 hover:text-brand dark:bg-background/70"
                        >
                            <span
                                class="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-brand-foreground shadow-sm"
                            >
                                <AppLogoIcon class="size-5 fill-current" />
                            </span>
                            <span>Revues structurees, acces simplifie</span>
                        </Link>
                    </div>

                    <div class="space-y-4">
                        <div class="space-y-4">
                            <h2
                                class="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:max-w-xl lg:text-5xl"
                            >
                                Passez de la litterature brute a un workflow
                                pret pour la revue.
                            </h2>
                            <p
                                class="max-w-xl text-sm leading-6 text-muted-foreground sm:text-base"
                            >
                                Connectez-vous pour reprendre votre screening,
                                integrer votre equipe plus vite et garder chaque
                                extraction dans un espace unique et tracable.
                            </p>
                        </div>
                    </div>

                    <div
                        class="grid gap-3 text-left sm:grid-cols-3 lg:max-w-2xl"
                    >
                        <div
                            class="rounded-2xl border border-border/60 bg-background/70 p-4 backdrop-blur-sm"
                        >
                            <p class="text-sm font-medium text-foreground">
                                Rigueur collaborative
                            </p>
                            <p class="mt-1 text-sm text-muted-foreground">
                                Alignez evaluateurs, decisions et notes dans un
                                meme flux.
                            </p>
                        </div>
                        <div
                            class="rounded-2xl border border-border/60 bg-background/70 p-4 backdrop-blur-sm"
                        >
                            <p class="text-sm font-medium text-foreground">
                                Extraction assistee
                            </p>
                            <p class="mt-1 text-sm text-muted-foreground">
                                Gardez l'aide IA visible, verifiable et
                                structuree.
                            </p>
                        </div>
                        <div
                            class="rounded-2xl border border-border/60 bg-background/70 p-4 backdrop-blur-sm"
                        >
                            <p class="text-sm font-medium text-foreground">
                                Syntheses tracables
                            </p>
                            <p class="mt-1 text-sm text-muted-foreground">
                                Conservez un chemin clair de l'import aux
                                livrables compatibles PRISMA.
                            </p>
                        </div>
                    </div>
                </div>

                <div class="w-full lg:justify-self-end">
                    <div
                        class="rounded-[2rem] border border-border/70 bg-background/85 p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl sm:p-8 dark:shadow-[0_24px_80px_rgba(2,8,23,0.45)]"
                    >
                        <div class="mb-8 space-y-4 text-center sm:text-left">
                            <div
                                class="flex justify-center sm:justify-start lg:hidden"
                            >
                                <Link
                                    :href="home()"
                                    class="inline-flex items-center gap-3 rounded-full border border-border/60 bg-background/80 px-4 py-2 text-sm font-medium text-foreground shadow-sm backdrop-blur-sm"
                                >
                                    <span
                                        class="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-brand-foreground"
                                    >
                                        <AppLogoIcon
                                            class="size-5 fill-current"
                                        />
                                    </span>
                                    <span class="sr-only">{{ title }}</span>
                                </Link>
                            </div>

                            <div class="space-y-3">
                                <div class="space-y-2">
                                    <h1
                                        class="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
                                    >
                                        {{ title }}
                                    </h1>
                                    <p
                                        class="text-sm leading-6 text-muted-foreground sm:text-base"
                                    >
                                        {{ description }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <slot />
                    </div>

                    <p
                        class="mt-4 text-center text-xs tracking-[0.14em] text-muted-foreground uppercase"
                    >
                        Verification d'email, sessions securisees et connexion
                        plus fluide.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
